import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { aiPodService, type AiPod } from '../services/aiPodService';
import {
  aiPodBillingService,
  formatMoney,
  isLiveStatus,
  planPriceForCycle,
  type PodSubscription,
} from '../services/aiPodBillingService';
import PodBreadcrumb from '../components/ai-pod/PodBreadcrumb';
import { APP_CONFIG } from '../constants';
import type { BillingPlanDto } from '../services/billingService';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Surface } from '../components/ui/Surface';
import { useThemeColor } from '../context/BrandingContext';

// ── helpers ──────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  '#7e5eb5', '#3683c6', '#4dcfbe', '#4bbae3', '#805dc6', '#b59be1',
];
function avatarBg(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

// ── metadata accessors ────────────────────────────────────────────────────────

// Content comes only from what the super admin entered; empty sections are hidden.
function podModeTags(pod: AiPod): string[] {
  const m = (pod.metadata as any)?.modes;
  if (Array.isArray(m) && m.length > 0) return m;
  // Without explicit tags, describe what the pod provisions.
  return pod.channel === 'chat' ? ['Chats'] : pod.channel === 'both' ? ['Chats', 'Automated'] : ['Automated'];
}
function podHighlights(pod: AiPod): Array<{ title: string; body: string }> {
  const h = (pod.metadata as any)?.highlights;
  return Array.isArray(h) ? h.filter((x) => x?.title) : [];
}
function podHowItWorks(pod: AiPod): Array<{ icon?: string; title: string; body: string }> {
  const h = (pod.metadata as any)?.howItWorks;
  return Array.isArray(h) ? h.filter((x) => x?.title) : [];
}
function podSidebarFeatures(pod: AiPod): string[] {
  const f = (pod.metadata as any)?.sidebarFeatures;
  return Array.isArray(f) ? f.filter(Boolean) : [];
}
function podPairWith(pod: AiPod): string | null {
  return (pod.metadata as any)?.pairWith ?? null;
}

// Split "Name — Role" or "Name - Role" into { name, role }
function splitAgentRole(raw: string): { name: string; role: string } {
  const sep = raw.indexOf(' — ');
  if (sep !== -1) return { name: raw.slice(0, sep).trim(), role: raw.slice(sep + 3).trim() };
  return { name: raw, role: '' };
}
/** Cheapest monthly price across plans (yearly-only plans count per month), or null. */
function fromPrice(plans: BillingPlanDto[]): { amountMinor: number; currency: string } | null {
  const prices = plans
    .map((p) => {
      const monthly = planPriceForCycle(p, 'monthly');
      if (monthly) return { amountMinor: monthly.amountMinor, currency: monthly.currency };
      const yearly = planPriceForCycle(p, 'yearly');
      return yearly ? { amountMinor: Math.round(yearly.amountMinor / 12), currency: yearly.currency } : null;
    })
    .filter((x): x is { amountMinor: number; currency: string } => !!x);
  if (!prices.length) return null;
  return prices.reduce((min, p) => (p.amountMinor < min.amountMinor ? p : min));
}

// Mode pill icons
function ModeIcon({ mode }: { mode: string }) {
  if (mode === 'Automated') return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
  if (mode === 'Chats') return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
  if (mode === 'Talks') return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
  return null;
}

// How it works icons
function HowIcon({ icon }: { icon: string }) {
  const cls = 'h-4 w-4 text-kv-agentic-blue';
  if (icon === 'automated' || icon === 'Automated') return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
  if (icon === 'chats' || icon === 'Chats') return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
  if (icon === 'person' || icon === 'needs') return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
  return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

// ── component ─────────────────────────────────────────────────────────────────

const PodDetail: React.FC = () => {
  // Avatar colours: the blue and purple ones follow the client's theme.
  const themeColor = useThemeColor();
  const { podId } = useParams<{ podId: string }>();
  const navigate = useNavigate();

  const [pod, setPod] = useState<AiPod | null>(null);
  const [plans, setPlans] = useState<BillingPlanDto[]>([]);
  const [subscription, setSubscription] = useState<PodSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!podId) return;
    setLoading(true);
    setFetchError(null);
    aiPodService.getAiPodById(podId)
      .then((p) => {
        setPod(p);
        return Promise.all([
          aiPodBillingService.listBillingPlans(podId)
            .then((r) => setPlans(r.plans || []))
            .catch(() => setPlans([])),
          aiPodBillingService.getSubscription(podId)
            .then(setSubscription)
            .catch(() => setSubscription(null)),
        ]);
      })
      .catch((err: unknown) => {
        setFetchError(err instanceof Error ? err.message : 'Failed to load pod');
      })
      .finally(() => setLoading(false));
  }, [podId]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-agentic-gray-50">
        <span className="loading-spinner" />
      </div>
    );
  }

  if (fetchError || !pod) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-agentic-gray-50">
        <p className="text-lg font-semibold text-agentic-text-primary">
          {fetchError ?? 'Pod not found'}
        </p>
        <Link to="/agent-types?tab=pods" className="text-sm text-agentic-primary-blue underline">
          Back to {APP_CONFIG.PODS_TAB_LABEL}
        </Link>
      </div>
    );
  }

  const orderedAgents = [...(pod.agentTypes ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);
  const modes = podModeTags(pod);
  const highlights = podHighlights(pod);
  const howItWorks = podHowItWorks(pod);
  const sidebarFeatures = podSidebarFeatures(pod);
  const pairWith = podPairWith(pod);
  const minPrice = fromPrice(plans);
  const maxTrialDays = Math.max(0, ...plans.map((p) => p.trialDays ?? 0));

  return (
    <div className="min-h-screen bg-agentic-gray-50">
      <div className="mx-auto max-w-6xl px-6 py-10">

        <PodBreadcrumb items={[{ label: pod.name }]} />

        <div className="flex flex-col gap-8 lg:flex-row lg:items-start">

          {/* ── Main content ── */}
          <div className="flex-1 min-w-0 space-y-8">

            {/* Hero */}
            <div className="flex items-start gap-5">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-kv-agentic-purple to-kv-agentic-blue text-white shadow-card">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="min-w-0">
                <h1 className="text-4xl font-bold text-kv-agentic-body leading-tight">{pod.name}</h1>
                {(pod.shortDescription || pod.description) && (
                  <p className="mt-1 text-sm leading-relaxed text-agentic-text-secondary">
                    {pod.shortDescription || pod.description}
                  </p>
                )}
                {modes.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {modes.map((m) => (
                      <Badge
                        key={m}
                        className="inline-flex items-center gap-1.5 rounded-full border border-agentic-gray-200 bg-white px-3 py-1 text-xs font-medium text-agentic-text-secondary shadow-sm"
                      >
                        <ModeIcon mode={m} />
                        {m}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Capability highlights */}
            {highlights.length > 0 && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {highlights.map((h, i) => (
                  <div key={i} className="rounded-panel bg-white p-5 shadow-card">
                    <p className="text-sm font-semibold text-agentic-text-primary">{h.title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-agentic-text-secondary">{h.body}</p>
                  </div>
                ))}
              </div>
            )}

            {/* How this Pod works */}
            {howItWorks.length > 0 && (
              <div>
                <h2 className="section-title mb-4">How this Pod works</h2>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {howItWorks.map((item, i) => (
                    <div key={i} className="rounded-panel bg-white p-5 shadow-card">
                      <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-kv-agentic-surface-lilac">
                        <HowIcon icon={item.icon ?? ""} />
                      </div>
                      <p className="text-sm font-semibold text-agentic-text-primary">{item.title}</p>
                      <p className="mt-1.5 text-sm leading-relaxed text-agentic-text-secondary">{item.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Meet your team */}
            {orderedAgents.length > 0 && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="section-title">Meet your team</h2>
                  <Badge className="rounded-full border border-agentic-gray-200 bg-white px-3 py-1 text-xs text-agentic-text-muted">
                    {orderedAgents.length} AI agent{orderedAgents.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {orderedAgents.map((ref) => {
                    const bg = themeColor(avatarBg(ref.agentType.name));
                    const { name: agentName, role } = splitAgentRole(ref.agentType.name);
                    const desc = ref.agentType.shortDescription || '';
                    return (
                      <div key={ref.id} className="rounded-panel bg-white p-4 shadow-card">
                        <div className="mb-3 flex items-center gap-3">
                          <div
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xs font-bold text-white"
                            style={{ background: bg }}
                          >
                            {initials(agentName)}
                          </div>
                          <div>
                            <p className="text-sm font-semibold leading-snug text-agentic-text-primary">{agentName}</p>
                            {role && (
                              <p className="text-xs text-agentic-text-muted">{role}</p>
                            )}
                          </div>
                        </div>
                        {desc && (
                          <p className="line-clamp-3 text-xs leading-relaxed text-agentic-text-secondary">{desc}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Sidebar ── */}
          <div className="w-full lg:w-[300px] shrink-0">
            <div className="sticky top-6 space-y-3">

              {/* Pricing card */}
              <Surface className="rounded-panel bg-white p-6 shadow-card border border-agentic-gray-100">
                {plans.length === 0 ? (
                  <div className="mb-5">
                    <p className="text-sm font-medium text-agentic-text-secondary">No plans are available for this pod yet.</p>
                  </div>
                ) : minPrice && minPrice.amountMinor > 0 ? (
                  <div className="mb-5">
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-agentic-text-muted">Plans from</p>
                    <div className="flex items-end gap-1">
                      <span className="text-4xl font-bold text-kv-agentic-body">{formatMoney(minPrice.amountMinor, minPrice.currency)}</span>
                      <span className="mb-1 text-sm text-agentic-text-muted">/month</span>
                    </div>
                  </div>
                ) : (
                  <div className="mb-5">
                    <p className="text-2xl font-bold text-kv-agentic-body">Free</p>
                  </div>
                )}

                {isLiveStatus(subscription?.status) ? (
                  <Button onClick={() => navigate(`/pods/${pod.id}/manage`)} variant="primary" size="md" className="w-full">
                    Manage subscription
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate(`/pods/${pod.id}/plans`)}
                    variant="primary"
                    size="md"
                    className="w-full"
                    disabled={plans.length === 0}
                  >
                    {subscription?.status === 'canceled' ? 'Subscribe again' : 'Get started'}
                  </Button>
                )}
                <p className="mt-2 text-center text-xs text-agentic-text-muted">
                  {maxTrialDays > 0 && !subscription?.trialUsedAt
                    ? `Up to ${maxTrialDays}-day free trial · Cancel anytime`
                    : 'Cancel anytime'}
                </p>

                {sidebarFeatures.length > 0 && (
                  <ul className="mt-5 space-y-2.5 border-t border-agentic-gray-100 pt-4">
                    {sidebarFeatures.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-agentic-text-primary">
                        <svg className="mt-0.5 h-4 w-4 shrink-0 text-kv-agentic-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                )}

                {plans.length > 0 && (
                  <div className="mt-4 text-center">
                    <Button tone="custom"
                      onClick={() => navigate(`/pods/${pod.id}/plans`)}
                      className="text-sm font-medium text-kv-agentic-purple underline underline-offset-2 hover:text-kv-agentic-blue transition-colors"
                    >
                      Compare plans
                    </Button>
                  </div>
                )}
              </Surface>

              {/* Pair with pod */}
              {pairWith && (
                <div className="rounded-panel bg-kv-agentic-surface-lilac p-4 flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 shrink-0 text-kv-agentic-purple" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <p className="text-sm leading-relaxed text-kv-agentic-slate">
                    Works well with <span className="font-semibold text-kv-agentic-body">{pairWith}</span>.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PodDetail;
